import type { QueryKey } from '@tanstack/react-query';

import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import utils from '../../../utils';
import type { Revision } from '../../../types';

/**
 * The maximum number of revisions to fetch.
 * This is the value that we've always used for the revision dropdown so we'll use it here as well.
 */
const maxRevisionCount = 12;

type GetRevisionHistoryParams = {
  revisionLocator: string;
  revisionLocatorFilter?: string;
  count?: number;
  source: string | null;
};

const getRevisionHistory = ({
  revisionLocator,
  revisionLocatorFilter,
  count = maxRevisionCount,
  source,
}: GetRevisionHistoryParams) => {
  return get<{revisions: Revision[]}>(`/api/revisions/${utils.nuc(revisionLocator)}/history`, false, {
    locator: revisionLocatorFilter,
    count,
    offset: 0,
    source,
  });
};

const getQueryKey = (params: GetRevisionHistoryParams): QueryKey => ['revision', params, 'history'];

/**
 * useRevisionHistory is a hook that fetches the revision history for a revision.
 */
const useRevisionHistory = (params: GetRevisionHistoryParams) => {
  return useQuery({
    queryKey: getQueryKey(params),
    queryFn: () => getRevisionHistory(params),
  });
};

export default useRevisionHistory;
